import type { TenderItem } from "@/types/tenders";

export const statuses = [
  { value: "new", label: "Nowy" },
  { value: "review", label: "Do analizy" },
  { value: "insurer", label: "Czekamy na TU" },
  { value: "preparing", label: "Przygotowanie oferty" },
  { value: "submitted", label: "Oferta złożona" },
  { value: "won", label: "Wygrany" },
  { value: "lost", label: "Przegrany" },
  { value: "rejected", label: "Rezygnujemy" },
];
export const checks = [
  ["documents", "Dokumentacja i wszystkie zmiany sprawdzone"],
  ["deadline", "Aktualne terminy ofert i pytań potwierdzone"],
  ["lots", "Możliwość osobnej oferty na pojazdy potwierdzona"],
  ["authorization", "Pełnomocnictwo i uprawnienia sprawdzone"],
  ["insurer", "Zgoda i wycena towarzystwa uzyskane"],
  ["protection", "Zakres, limity i klauzule zaakceptowane"],
  ["attachments", "Formularze i załączniki skompletowane"],
  ["signature", "Podpis i sposób złożenia oferty sprawdzone"],
] as const;
export const provinces: Record<string, string> = {
  PL02: "dolnośląskie",
  PL04: "kujawsko-pomorskie",
  PL06: "lubelskie",
  PL08: "lubuskie",
  PL10: "łódzkie",
  PL12: "małopolskie",
  PL14: "mazowieckie",
  PL16: "opolskie",
  PL18: "podkarpackie",
  PL20: "podlaskie",
  PL22: "pomorskie",
  PL24: "śląskie",
  PL26: "świętokrzyskie",
  PL28: "warmińsko-mazurskie",
  PL30: "wielkopolskie",
  PL32: "zachodniopomorskie",
};
export function dateLabel(value: string | null) {
  return value
    ? new Date(value).toLocaleString("pl-PL", {
        timeZone: "Europe/Warsaw",
        dateStyle: "short",
        timeStyle: "short",
      })
    : "Nieustalony";
}
export function isExpired(item: TenderItem, now = Date.now()) {
  return !!item.source.deadline && Date.parse(item.source.deadline) <= now;
}
export function safeUrl(value: string | null) {
  try {
    const url = new URL(value ?? "");
    return ["http:", "https:"].includes(url.protocol) &&
      !url.username &&
      !url.password
      ? url.href
      : null;
  } catch {
    return null;
  }
}
export function download(blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
export function exportCsv(items: TenderItem[]) {
  const cell = (value: string) =>
    `"${(/^[\s]*[=+@-]/.test(value) ? `'${value}` : value).replaceAll('"', '""')}"`;
  const rows = [
    [
      "Numer",
      "Przetarg",
      "Organizacja",
      "Termin z ogłoszenia",
      "Status",
      "Prowadzący",
      "Sugestia",
      "Oryginał",
    ],
    ...items.map((i) => [
      i.source.number,
      i.source.title,
      i.source.organization,
      dateLabel(i.source.deadline),
      statuses.find((s) => s.value === i.work.status)?.label ?? i.work.status,
      i.work.owner,
      i.source.suggestion,
      safeUrl(i.source.originalUrl) ?? "",
    ]),
  ];
  download(
    new Blob(
      [`\ufeff${rows.map((row) => row.map(cell).join(";")).join("\r\n")}`],
      { type: "text/csv;charset=utf-8" },
    ),
    "adara-przetargi.csv",
  );
}
export function calendar(item: TenderItem) {
  if (!item.source.deadline) {
    return;
  }
  const escapeIcs = (s: string) =>
    s
      .replaceAll("\\", "\\\\")
      .replaceAll("\n", "\\n")
      .replaceAll(",", "\\,")
      .replaceAll(";", "\\;")
      .replaceAll("\r", "");
  const dt = (s: string) =>
    new Date(s)
      .toISOString()
      .replace(/[-:]/g, "")
      .replace(/\.\d{3}/, "");
  const lines = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Adara//Przetargi//PL",
    "BEGIN:VEVENT",
    `UID:${item.source.id}@adara`,
    `DTSTAMP:${dt(new Date().toISOString())}`,
    `DTSTART:${dt(item.source.deadline)}`,
    `SUMMARY:${escapeIcs(`Sprawdź termin oferty: ${item.source.title}`)}`,
    `DESCRIPTION:${escapeIcs(`Termin z ogłoszenia — potwierdź w oryginalnym postępowaniu. ${safeUrl(item.source.originalUrl) ?? ""}`)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  download(
    new Blob([lines.join("\r\n")], { type: "text/calendar" }),
    "termin-przetargu.ics",
  );
}
export function insurerBrief(item: TenderItem) {
  const s = item.source;
  return `Roboczy pakiet do towarzystwa — do sprawdzenia\n\n${s.title}\nNumer: ${s.number}\nOrganizacja: ${s.organization}\nTermin z ogłoszenia: ${dateLabel(s.deadline)}\nOryginał: ${safeUrl(s.originalUrl) ?? "nieustalony"}\n\nProsimy o ocenę możliwości udziału, akceptacji ryzyka i przygotowania wyceny. Prosimy również o potwierdzenie umocowania Adary, zasad obsługi i wynagrodzenia.\n\nDo wyjaśnienia: ${item.work.nextStep || "podział na części, zakres ochrony, aktualne dokumenty i terminy"}\nTU: ${item.work.insurer || "do ustalenia"}\n\nStatus dokumentacji: ${s.documentStatus}\n\nChecklistę i fragmenty wymagań trzeba zweryfikować w pełnej, aktualnej SWZ. Ten tekst nie jest ofertą.`;
}
